//
// Source of truth: TAGS-API-CONTRACT.md § 2.3 + § 13.6
// Mirror of the backend adapter registry at:
//   controllers/Tags/shared/adapters.js
//
// If the backend adds/removes a resourceType, update this file manually until
// GET /tags/resource-types exists.
//
// Notifications V2 uses `notificationResourceType` (unprefixed API wire) for
// group assignments and resourceListers.ts keys. Tags Manager may use
// `pickerServiceType` when the wizard key differs from the Tags API wire value.

export type TagModule = "kontrol-edge" | "reflector-v2" | "reflector-v1";

export interface TaggableResourceType {
  /** Tags API wire format (kebab-case with prefix). URLs and query params. */
  resourceType: string;
  module: TagModule;
  /** Dropdown / table label for this resource kind. */
  label: string;
  /** Product subtitle (e.g. "Kontrol Edge"). */
  moduleLabel: string;
  /** Backend UPPER_SNAKE — reference only; never send on HTTP. */
  backendType: string;
  /** Singular form for picker copy. */
  singularLabel: string;
  /**
   * Notifications V2 `resourceType` on `/v1/notifications-v2/...`.
   * Omit when not supported in the notifications API yet.
   */
  notificationResourceType?: string;
  /**
   * Tags Manager wizard `serviceType` when it differs from `resourceType`
   * (e.g. UI `ke-datacenter-interconnect` → wire `ke-dci`).
   */
  pickerServiceType?: string;
}

/** Legacy Notifications assignment `resourceType` → canonical type. */
export const NOTIFICATION_RESOURCE_TYPE_ALIASES: Record<string, string> = {
  bbaas: "backbone",
};

export const TAG_RESOURCE_TYPES: readonly TaggableResourceType[] = [
  // ─────────────── Kontrol Edge ───────────────
  {
    resourceType: "ke-port",
    module: "kontrol-edge",
    label: "Ports",
    moduleLabel: "Kontrol Edge",
    backendType: "KE_PORT",
    singularLabel: "Port",
    notificationResourceType: "port",
  },
  {
    resourceType: "ke-internet",
    module: "kontrol-edge",
    label: "Internet Connectivity",
    moduleLabel: "Kontrol Edge",
    backendType: "KE_INTERNET",
    singularLabel: "Internet",
    notificationResourceType: "internet",
  },
  {
    resourceType: "ke-dci",
    module: "kontrol-edge",
    label: "Data Center Interconnect",
    moduleLabel: "Kontrol Edge",
    backendType: "KE_DCI",
    singularLabel: "DCI",
    notificationResourceType: "datacenter-interconnect",
    pickerServiceType: "ke-datacenter-interconnect",
  },
  {
    resourceType: "ke-cloud-connectivity",
    module: "kontrol-edge",
    label: "Cloud Connectivity",
    moduleLabel: "Kontrol Edge",
    backendType: "KE_CLOUD_CONNECTIVITY",
    singularLabel: "Cloud Connection",
    notificationResourceType: "cloud-connectivity",
  },
  {
    resourceType: "ke-backbone",
    module: "kontrol-edge",
    label: "Backbone",
    moduleLabel: "Kontrol Edge",
    backendType: "KE_BACKBONE",
    singularLabel: "Backbone",
    notificationResourceType: "backbone",
  },
  // {
  //   resourceType: "ke-bb-connector",
  //   module: "kontrol-edge",
  //   label: "BB Connectors",
  //   moduleLabel: "Kontrol Edge",
  //   backendType: "KE_BB_CONNECTOR",
  //   singularLabel: "BB Connector",
  //   notificationResourceType: "bb-connector",
  // },
  // {
  //   resourceType: "ke-vrf",
  //   module: "kontrol-edge",
  //   label: "VRFs",
  //   moduleLabel: "Kontrol Edge",
  //   backendType: "KE_VRF",
  //   singularLabel: "VRF",
  //   notificationResourceType: "vrf",
  // },
  {
    resourceType: "ke-fwaas",
    module: "kontrol-edge",
    label: "Virtual Firewall",
    moduleLabel: "Kontrol Edge",
    backendType: "KE_FWAAS",
    singularLabel: "Virtual Firewall",
    notificationResourceType: "fwaas",
  },
  {
    resourceType: "ke-sdwan-hub",
    module: "kontrol-edge",
    label: "SD-WAN",
    moduleLabel: "Kontrol Edge",
    backendType: "KE_SDWAN_HUB",
    singularLabel: "SD-WAN",
    notificationResourceType: "sdwan-hub",
  },
  {
    // Notifications V2 has no extender-pop resource yet, so no
    // notificationResourceType until that catalog grows one (CTX-364).
    resourceType: "ke-extender-pop",
    module: "kontrol-edge",
    label: "Extended PoP",
    moduleLabel: "Kontrol Edge",
    backendType: "KE_EXTENDER_POP",
    singularLabel: "Extended PoP",
  },
  {
    // Each router (CPE) of an Extended PoP site, tagged on its own
    // (CTX-832). Same as the site: no Notifications V2 resource yet.
    resourceType: "ke-extender-pop-cpe",
    module: "kontrol-edge",
    label: "Extended PoP CPEs",
    moduleLabel: "Kontrol Edge",
    backendType: "KE_EXTENDER_POP_CPE",
    singularLabel: "Extended PoP CPE",
  },
  {
    // Same as Extended PoP: no Carrier Access resource in Notifications V2
    // yet, so no notificationResourceType (CTX-654).
    resourceType: "ke-carrier-access",
    module: "kontrol-edge",
    label: "Carrier Access",
    moduleLabel: "Kontrol Edge",
    backendType: "KE_CARRIER_ACCESS",
    singularLabel: "Carrier Access",
  },

  // ─────────────── Reflector V2 (2 contract + extensions) ───────────────
  {
    resourceType: "rv2-instance",
    module: "reflector-v2",
    label: "Reflector Instances",
    moduleLabel: "CTX Reflector V2",
    backendType: "RV2_INSTANCE",
    singularLabel: "Reflector",
    notificationResourceType: "reflector",
    pickerServiceType: "rv2-reflector",
  },
  {
    resourceType: "rv2-cluster",
    module: "reflector-v2",
    label: "Reflector Clusters",
    moduleLabel: "CTX Reflector V2",
    backendType: "RV2_CLUSTER",
    singularLabel: "Cluster",
    notificationResourceType: "cluster",
  },
  {
    resourceType: "rv2-bgp-session",
    module: "reflector-v2",
    label: "BGP Sessions",
    moduleLabel: "CTX Reflector V2",
    backendType: "RV2_BGP_SESSION",
    singularLabel: "BGP Session",
    notificationResourceType: "bgp-session",
  },

  // ─────────────── Reflector V1 (1 contract + extensions) ───────────────
  {
    resourceType: "rv1-bgp-neighbor",
    module: "reflector-v1",
    label: "BGP Neighbors",
    moduleLabel: "CTX Reflector V1",
    backendType: "RV1_BGP_NEIGHBOR",
    singularLabel: "BGP Neighbor",
    notificationResourceType: "rv1-bgp-neighbor",
  },
  {
    resourceType: "rv1-instance",
    module: "reflector-v1",
    label: "Instances",
    moduleLabel: "CTX Reflector V1",
    backendType: "RV1_INSTANCE",
    singularLabel: "Instance",
    notificationResourceType: "rv1-instance",
  },
  {
    resourceType: "rv1-license",
    module: "reflector-v1",
    label: "Licenses",
    moduleLabel: "CTX Reflector V1",
    backendType: "RV1_LICENSE",
    singularLabel: "License",
    notificationResourceType: "rv1-license",
  },
] as const;

/** HTTP mount prefix per module (Tag Manager). */
export const TAG_API_PREFIX: Record<TagModule, string> = {
  "kontrol-edge": "/api/v1/kontroledge",
  "reflector-v2": "/api/v2/reflector",
  "reflector-v1": "/api/v1/ctxr",
};

export const TAG_RESOURCE_TYPE_BY_KEY: ReadonlyMap<string, TaggableResourceType> =
  new Map(TAG_RESOURCE_TYPES.map((t) => [t.resourceType, t]));

function hasPickerServiceType(
  row: TaggableResourceType
): row is TaggableResourceType & { pickerServiceType: string } {
  return typeof row.pickerServiceType === "string" && row.pickerServiceType.length > 0;
}

/** Tags Manager wizard keys that differ from `resourceType`. */
export const TAG_RESOURCE_TYPE_BY_PICKER_KEY: ReadonlyMap<
  string,
  TaggableResourceType
> = new Map(
  TAG_RESOURCE_TYPES.filter(hasPickerServiceType).map((row) => [
    row.pickerServiceType,
    row,
  ])
);

export const TAG_RESOURCE_TYPES_BY_MODULE: Record<
  TagModule,
  TaggableResourceType[]
> = {
  "kontrol-edge": TAG_RESOURCE_TYPES.filter((t) => t.module === "kontrol-edge"),
  "reflector-v2": TAG_RESOURCE_TYPES.filter((t) => t.module === "reflector-v2"),
  "reflector-v1": TAG_RESOURCE_TYPES.filter((t) => t.module === "reflector-v1"),
};

export function tagApiPrefix(resourceType: string): string | null {
  const t = TAG_RESOURCE_TYPE_BY_KEY.get(resourceType);
  return t ? TAG_API_PREFIX[t.module] : null;
}

export function normalizeNotificationResourceType(resourceType: string): string {
  return NOTIFICATION_RESOURCE_TYPE_ALIASES[resourceType] ?? resourceType;
}

/** Tags wire or picker `serviceType` → Notifications V2 `resourceType`. */
export function tagWireToNotificationResourceType(
  wireOrPickerKey: string
): string | null {
  const entry =
    TAG_RESOURCE_TYPE_BY_KEY.get(wireOrPickerKey) ??
    TAG_RESOURCE_TYPE_BY_PICKER_KEY.get(wireOrPickerKey);
  const notificationType = entry?.notificationResourceType;
  if (!notificationType) {
    return NOTIFICATION_RESOURCE_TYPE_ALIASES[wireOrPickerKey] ?? null;
  }
  return normalizeNotificationResourceType(notificationType);
}

/** Catalog row guaranteed to include `notificationResourceType`. */
export type NotificationResourceCatalogEntry = TaggableResourceType & {
  notificationResourceType: string;
};

/** Notifications API types that have a catalog row (for merged service dropdown). */
export function listNotificationResourceCatalogEntries(): NotificationResourceCatalogEntry[] {
  const seen = new Set<string>();
  const out: NotificationResourceCatalogEntry[] = [];
  for (const row of TAG_RESOURCE_TYPES) {
    const nrt = row.notificationResourceType;
    if (!nrt || seen.has(nrt)) continue;
    seen.add(nrt);
    out.push({ ...row, notificationResourceType: nrt });
  }
  return out;
}

/** Labels keyed by Notifications V2 `resourceType`. */
export function buildNotificationResourceTypeLabels(): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const row of TAG_RESOURCE_TYPES) {
    if (row.notificationResourceType) {
      labels[row.notificationResourceType] = row.singularLabel;
    }
  }
  return labels;
}
